import { isDefined, isNotDefined } from '@sgme/fp';
import { useContext } from 'react';

import { useTrackEvent } from '@/App/Blotter/useTrackEvent.ts';
import { OrderRow } from '@/App/tiles/orders/components/OrderRow.tsx';
import { FixingContext, useValidateFixing } from '@/App/tiles/orders/FixingOrder/FixingContext.tsx';
import { SgNumberInput } from '@/components/sg-design-system/components/input/SgNumberInput';
import { OmsFieldErrorWrapper } from '@/components/ui/OmsFieldErrorWrapper.tsx';
import { useAppSelector } from '@/store/hooks.ts';
import {
  selectFixingErrors,
  selectOrderEntryByIdFromBlotter,
  selectTradeIdFromFixingTile,
} from '@/store/orders/fixing/selectors.ts';

type FixingCommercialMarginProps = {
  commercialMargin: number | undefined;
  setCommercialMargin: (value: number | undefined) => void;
};

export const FixingEditingCommercialMargin = (props: FixingCommercialMarginProps) => {
  const { commercialMargin, setCommercialMargin } = props;
  const { tileId, setIsModifyingField } = useContext(FixingContext);
  const track = useTrackEvent();

  const tradeId = useAppSelector((state) => selectTradeIdFromFixingTile(state, tileId));
  const orderFromBlotter = useAppSelector((state) =>
    tradeId ? selectOrderEntryByIdFromBlotter(state, tradeId) : undefined,
  );

  const errors = useAppSelector((state) => selectFixingErrors(state, tileId));
  const commercialMarginError = errors?.commercialMargin;

  const validateFixing = useValidateFixing();

  const handleCommercialMarginChange = async (value: number | undefined) => {
    track('Order action', 'Commercial margin changed');
    if (isDefined(orderFromBlotter)) {
      const [currency1, currency2] = isDefined(orderFromBlotter?.currencyPair)
        ? orderFromBlotter.currencyPair.split('/')
        : ['', ''];

      if (isNotDefined(currency1) || isNotDefined(currency2)) {
        console.error(`[handleCommercialMarginChange] Invalid currency pair: ${currency1}/${currency2}`);
        return;
      }

      const response = await validateFixing({
        commercialMargin: value,
      });

      if (response?.data?.validationResponseDetailContent.length === 0) {
        setCommercialMargin(value);
      }
    }
  };

  return (
    <OrderRow labelId="order.fields.commercialMargin.label">
      <OmsFieldErrorWrapper errors={commercialMarginError}>
        <div className="input-group input-group-sm flex-nowrap">
          <SgNumberInput
            valuePosition="start"
            size="sm"
            maxDecimals={9}
            value={
              commercialMargin ??
              (isDefined(orderFromBlotter?.marginInBps) ? (orderFromBlotter?.marginInBps as number) : undefined)
            }
            data-e2e="commercial-margin"
            onChange={handleCommercialMarginChange}
            onFocus={() => setIsModifyingField(true)}
            onBlur={() => setIsModifyingField(false)}
            selectAllOnFocus
            state={isDefined(commercialMarginError) ? 'invalid' : undefined}
          />
          <span className="input-group-text">Bps</span>
        </div>
      </OmsFieldErrorWrapper>
    </OrderRow>
  );
};
